<script setup lang="ts">
import { useQuery } from "@tanstack/vue-query";
import { storeToRefs } from "pinia";
import KenticoClientContent from "~/components/global/kentico/components/KenticoClientContent.vue";
import LiveIndicatorDot from "~/components/layout/Sport/LiveIndicatorDot.vue";
import LiveIndicatorDotDisabled from "~/components/layout/Sport/LiveIndicatorDotDisabled.vue";
import EventFiltersModal from "~/components/layout/Sport/SportsBook/EventFilters/Modal/index.vue";
import EventFilters from "~/components/layout/Sport/SportsBook/EventFilters/index.vue";
import EventTopLeagues from "~/components/layout/Sport/SportsBook/EventTopLeagues.vue";
import Tabs from "~/components/ui/Tabs.vue";
import { signalREndpoints, useSignalR } from "~/composables/SignalR/useSignalR";
import useFilterStorage from "~/composables/Sport/useFilterStorage";
import UseCollapsedView from "~/composables/useCollapsedView";
import { useBetbook } from "~/store/betbook";
import { useBetslip } from "~/store/betslip";
import { useSettings } from "~/store/settings";
import { IRegion } from "~/types/sports/region";
import { ISport } from "~/types/sports/sport";
import api from "~/utils/auth/api";
import { isMobile } from "~/utils/constants";
import sportUrlBuilder from "~/utils/sport/sportUrlBuilder";
import t from "~/utils/translate";
import FixtureType from "./[sportId]/[fixtureType].vue";

interface Props {
	sports: { sports: ISport[] } | undefined;
	isSportsLoading: boolean;
	isEsports: boolean;
	liveOnly?: boolean;
}
const props = defineProps<Props>();

const route = useRoute();
const betbookStore = useBetbook();
const router = useRouter();
const isCollapseedVIew = UseCollapsedView();
const settings = useSettings();
const betslip = useBetslip();
const { personalizedMenu } = storeToRefs(betslip);

const sportId = computed(() => {
	if (route.params.sportId) return route.params.sportId;
	if (props.liveOnly && !route.params.sportId) return props.sports?.sports.find((x) => x.liveInPlayCount > 0)?.sportId;
	if (props.sports) {
		return props.sports.sports.filter((x) => x.filterMarkets !== undefined)[0].sportId;
	}
});

useQuery({
	queryKey: ["sport-regions", sportId],
	queryFn: () =>
		api<{ regions: IRegion[] }>({
			url: `/Feeds/RegionsAndLeagues/${sportId.value}?countryCode={brand}`,
			apiVersion: "v1",
			type: "sports",
		}).then((res) => {
			betbookStore.setRegions(res.regions);
			return res;
		}),
	enabled: computed(() => !!sportId.value),
});

useFilterStorage(sportId.value);
const fixtureType = computed(() => route.params.fixtureType || (props.liveOnly ? "live" : "highlights"));

const sport = computed(() => {
	if (props.sports?.sports[0]) {
		const val = sportId ? props.sports.sports.find((x) => x.sportId === sportId.value) : props.sports.sports[0];
		if (val?.filterMarkets !== betbookStore.filters.markets) {
			betbookStore.filters.markets = val?.filterMarkets || [];
			betbookStore.calculateDisplayMarkets(val?.filterMarkets || []);
		}
		return val;
	}
});

const handleRetry = () => {
	window.location.reload();
};

const handleFiltersClose = (hasChanges: boolean) => {
	betbookStore.filterModal = false;
};

const ledger = useLedger();
const betbookSignalR = useSignalR("betBook");
onBeforeUnmount(() => {
	const ledgerData = ledger.get("events", "betBook");
	if (ledgerData) {
		betbookSignalR.unSubscribe(signalREndpoints.unsubscriptionBetbookEndpoint, {
			eventIds: ledgerData.events,
			marketTypes: ledgerData.markets,
			countryCode: settings.getCountryConfig?.countryIsoTwo,
		});
	}
});

watchEffect(() => {
	if (!sport.value && !props.isSportsLoading) showError({ statusCode: 404, statusMessage: "Page Not Found" });
});

watchEffect(() => {
	const matchedSport = props.sports?.sports.find((x) => x.sportId === sportId.value?.toString());
	if (sportId.value && !personalizedMenu.value.includes(sportId.value.toString()) && matchedSport && !matchedSport.shouldDisplayCarousel) {
		betslip.personalizedMenu.push(sportId.value.toString());
	}
});

const hasCollapsedView = computed(() => isMobile && settings.hasFeature("IsCollapsedViewEnabled"));
</script>

<template>
	<div id="sports-container" class="flex flex-col gap-2" v-if="sport || isSportsLoading">
		<NuxtErrorBoundary>
			<template #error="{ error }">
				<div class="flex flex-col items-center justify-center w-full gap-2 my-6 text-center">
					<strong>Unexpected Error</strong>
					{{ console.log(error) }}
					<p class="text-xs">
						{{ error }}
					</p>
					<UiButton label="Try Again" @click="handleRetry()" class="w-48" />
				</div>
			</template>
			<EventFiltersModal
				v-if="betbookStore.filterModal"
				:sport="sport"
				@close="handleFiltersClose"
				@go-to-highlights="
					(query) => {
						fixtureType = 'highlights';
						router
							.push({
								path: sportUrlBuilder({ sport: sport?.sportId, fixtureType: 'highlights' }),
								query,
							})
							.then(() => (betbookStore.applyingFilters = false));
					}
				"
			/>
			<EventTopLeagues v-if="sport && !settings.simpleView && !isCollapseedVIew" :sport="sport" />
			<Tabs
				v-if="!isCollapseedVIew"
				id="sports-tabs"
				:key="(fixtureType as string)"
				class="text-sm"
				tab-class="justify-center flex-1"
				:active="(fixtureType as string)"
				@on-update="
					(selected) => {
						betbookStore.applyingFilters = true;
						betbookStore.resetPagination();
						router
							.push({
								path: sportUrlBuilder({ sport: sport?.sportId, fixtureType: selected }, undefined, hasCollapsedView),
							})
							.then(() => (betbookStore.applyingFilters = false));
					}
				"
				:tabs="[
					{
						name: t('live'),
						value: 'live',
						icon: sport?.hasLiveInPlayEvents ? LiveIndicatorDot : LiveIndicatorDotDisabled,
						disabled: !sport?.hasLiveInPlayEvents || (!sport?.hasHighlights && !sport?.hasUpcomingEvents),
					},
					{ name: t('highlights'), value: 'highlights', disabled: !sport?.hasHighlights },
					{ name: t('upcoming'), value: 'upcoming', disabled: !sport?.hasUpcomingEvents },
					{ name: t('outrights'), value: 'outrights', disabled: !sport?.hasOutrights },
				]"
			/>
			<EventFilters
				v-if="fixtureType !== 'outrights' && !settings.simpleView"
				:fixtureType="(fixtureType as string)"
				:isLoading="!sport || isSportsLoading"
				:sport="sport"
			/>
			<NuxtPage v-if="route.params.fixtureType" :fixtureType="(fixtureType as string)" :isEsports="isEsports" :sport="sport" />
			<FixtureType v-else :fixtureType="liveOnly ? 'live' : (fixtureType as string)" :isEsports="isEsports" :sport="sport" />
		</NuxtErrorBoundary>
		<DataFreeGuard>
			<div class="mt-8">
				<KenticoClientContent v-if="!settings.isNativeApp" />
			</div>
		</DataFreeGuard>
	</div>
</template>
